// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { formatDistanceToNow } from "date-fns";
import {
  AlertTriangle,
  Bell,
  BellRing,
  Check,
  CircleAlert,
  Construction,
  Info,
  PackageCheck,
  Settings,
  UserPlus,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { useUnreadNotificationCount } from "@/lib/notifications/unread-count-store";
import { cn } from "@/lib/utils";

type NotificationType = "info" | "warning" | "action";

interface Notification {
  id: string;
  type: NotificationType;
  titleKey: string;
  descriptionKey: string;
  timestamp: Date;
  read: boolean;
  actionLabelKey?: string;
  onAction?: () => void;
}

const NOTIFICATION_ICONS: Record<NotificationType, typeof Info> = {
  info: Info,
  warning: AlertTriangle,
  action: CircleAlert,
};

const NOTIFICATION_COLORS: Record<NotificationType, string> = {
  info: "text-blue-500",
  warning: "text-amber-500",
  action: "text-primary",
};

function createMockNotifications(): Notification[] {
  return [
    {
      id: "1",
      type: "action",
      titleKey: "notifications.mock.importFailed.title",
      descriptionKey: "notifications.mock.importFailed.description",
      timestamp: new Date(Date.now() - 1000 * 60 * 12),
      read: false,
      actionLabelKey: "notifications.actions.review",
    },
    {
      id: "2",
      type: "action",
      titleKey: "notifications.mock.settingApproval.title",
      descriptionKey: "notifications.mock.settingApproval.description",
      timestamp: new Date(Date.now() - 1000 * 60 * 45),
      read: false,
      actionLabelKey: "notifications.actions.approve",
    },
    {
      id: "3",
      type: "info",
      titleKey: "notifications.mock.importComplete.title",
      descriptionKey: "notifications.mock.importComplete.description",
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
      read: false,
    },
    {
      id: "4",
      type: "warning",
      titleKey: "notifications.mock.storeOffline.title",
      descriptionKey: "notifications.mock.storeOffline.description",
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 3),
      read: true,
    },
    {
      id: "5",
      type: "info",
      titleKey: "notifications.mock.userCreated.title",
      descriptionKey: "notifications.mock.userCreated.description",
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5),
      read: true,
    },
    {
      id: "6",
      type: "info",
      titleKey: "notifications.mock.settingUpdated.title",
      descriptionKey: "notifications.mock.settingUpdated.description",
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
      read: true,
    },
  ];
}

const MOCK_ICONS: Record<string, typeof Info> = {
  "1": CircleAlert,
  "2": Settings,
  "3": PackageCheck,
  "4": AlertTriangle,
  "5": UserPlus,
  "6": Settings,
};

const MOCK_COLORS: Record<string, string> = {
  "1": "text-destructive",
};

export function NotificationPanel() {
  const { t } = useTranslation("back-office");
  const [notifications, setNotifications] = useState(createMockNotifications);
  const [open, setOpen] = useState(false);
  const headerUnreadCount = useUnreadNotificationCount();

  // Use header-reported count when available (non-zero), otherwise derive from local state
  const unreadCount = headerUnreadCount > 0 ? headerUnreadCount : notifications.filter((n) => !n.read).length;

  function markAsRead(id: string) {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }

  function markAllAsRead() {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }

  function dismissNotification(id: string) {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative inline-flex">
          <Button variant="ghost" size="icon" className="h-8 w-8">
            {unreadCount > 0 ? <BellRing className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
            <span className="sr-only">{t("notifications.title")}</span>
          </Button>
          {unreadCount > 0 && (
            <span className="pointer-events-none absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
              {unreadCount}
            </span>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={-32} className="w-96 p-0">
        <div className="flex items-center justify-between px-4 py-[0.575rem] rounded-t-md bg-sidebar">
          <h3 className="flex h-6 items-center text-sm font-semibold">{t("notifications.title")}</h3>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="h-6 px-2 py-1 text-xs font-normal" onClick={markAllAsRead}>
              {t("notifications.markAllRead")}
            </Button>
          )}
        </div>
        <Separator />
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-400 text-xs">
          <Construction className="h-3.5 w-3.5 shrink-0" />
          {t("notifications.wipBanner")}
        </div>
        <Separator />
        <div className="max-h-96 overflow-y-auto rounded-b-md">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-sm text-muted-foreground">
              <Bell className="mb-2 h-8 w-8 opacity-30" />
              {t("notifications.empty")}
            </div>
          ) : (
            <div className="flex flex-col">
              {notifications.map((notification) => {
                const Icon = MOCK_ICONS[notification.id] ?? NOTIFICATION_ICONS[notification.type];
                return (
                  <div
                    key={notification.id}
                    className={cn(
                      "flex cursor-default gap-3 px-4 py-3 hover:bg-accent hover:text-accent-foreground",
                      !notification.read && "bg-muted/30",
                    )}
                  >
                    <div
                      className={cn(
                        "mt-0.5 shrink-0",
                        MOCK_COLORS[notification.id] ?? NOTIFICATION_COLORS[notification.type],
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className={cn("text-sm leading-tight", !notification.read && "font-medium")}>
                          {t(notification.titleKey)}
                        </p>
                        {!notification.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />}
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">{t(notification.descriptionKey)}</p>
                      <div className="mt-1.5 flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(notification.timestamp, { addSuffix: true })}
                        </span>
                        {notification.actionLabelKey && (
                          <Button
                            size="xs"
                            className="text-xs"
                            onClick={() => {
                              notification.onAction?.();
                              markAsRead(notification.id);
                            }}
                          >
                            {t(notification.actionLabelKey)}
                          </Button>
                        )}
                        {notification.read && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-auto px-1.5 py-0.5 text-xs text-muted-foreground"
                            onClick={() => dismissNotification(notification.id)}
                          >
                            {t("notifications.dismiss")}
                          </Button>
                        )}
                        {!notification.read && !notification.actionLabelKey && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-auto gap-1 px-1.5 py-0.5 text-xs text-muted-foreground"
                            onClick={() => markAsRead(notification.id)}
                          >
                            <Check className="h-3 w-3" />
                            {t("notifications.markRead")}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
